/**
 * Type catalogs for the abstract floorplan editor.
 *
 * Values mirror `server/apps/reunification/constants.py` (ADR-064).
 * Keep the two lists in sync — the backend rejects unknown choices.
 */
import type { IconType } from 'react-icons';
import {
  HiHandRaised,
  HiClipboardDocumentCheck,
  HiClock,
  HiShieldExclamation,
  HiHeart,
  HiUserGroup,
  HiIdentification,
  HiChatBubbleLeftRight,
  HiHome,
  HiArrowRightOnRectangle,
  HiPlusCircle,
  HiFlag,
  HiVideoCamera,
  HiTruck,
  HiQuestionMarkCircle,
} from 'react-icons/hi2';

export interface StationTypeOption {
  value: string;
  label: string;
  icon: IconType;
}

export const STATION_TYPE_OPTIONS: StationTypeOption[] = [
  { value: 'greeter', label: 'Greeter', icon: HiHandRaised },
  { value: 'check_in', label: 'Check-In', icon: HiClipboardDocumentCheck },
  {
    value: 'waiting_standard',
    label: 'Waiting Area (Standard)',
    icon: HiClock,
  },
  {
    value: 'waiting_predator',
    label: 'Waiting Area (Predator/Custody)',
    icon: HiShieldExclamation,
  },
  {
    value: 'waiting_hospital',
    label: 'Waiting Area (Hospital)',
    icon: HiHeart,
  },
  { value: 'assembly', label: 'Assembly Area', icon: HiUserGroup },
  { value: 'leo_interview', label: 'LEO Interview', icon: HiIdentification },
  {
    value: 'crisis_counseling',
    label: 'Crisis Counseling',
    icon: HiChatBubbleLeftRight,
  },
  { value: 'reunification_room', label: 'Reunification Room', icon: HiHome },
  { value: 'exit', label: 'Exit Checkpoint', icon: HiArrowRightOnRectangle },
  { value: 'medical', label: 'Medical/First Aid', icon: HiPlusCircle },
  { value: 'command_post', label: 'Command Post', icon: HiFlag },
  { value: 'media_staging', label: 'Media Staging', icon: HiVideoCamera },
  { value: 'bus_staging', label: 'Bus Staging', icon: HiTruck },
  { value: 'other', label: 'Other', icon: HiQuestionMarkCircle },
];

export interface ZoneTypeOption {
  value: string;
  label: string;
  /** Default fill assigned when the zone is drawn; user-overridable. */
  defaultColor: string;
}

export const ZONE_TYPE_OPTIONS: ZoneTypeOption[] = [
  {
    value: 'student_assembly',
    label: 'Student Assembly',
    defaultColor: '#3182CE',
  },
  {
    value: 'student_holding',
    label: 'Student Holding',
    defaultColor: '#63B3ED',
  },
  { value: 'parent_parking', label: 'Parent Parking', defaultColor: '#38A169' },
  { value: 'staff_parking', label: 'Staff Parking', defaultColor: '#68D391' },
  {
    value: 'emergency_parking',
    label: 'Emergency Vehicle Parking',
    defaultColor: '#E53E3E',
  },
  { value: 'bus_loading', label: 'Bus Loading', defaultColor: '#D69E2E' },
  {
    value: 'parent_entrance',
    label: 'Parent Entrance',
    defaultColor: '#805AD5',
  },
  { value: 'staff_entrance', label: 'Staff Entrance', defaultColor: '#B794F4' },
  {
    value: 'emergency_entrance',
    label: 'Emergency Entrance',
    defaultColor: '#F56565',
  },
  { value: 'restricted', label: 'Restricted Area', defaultColor: '#822727' },
  { value: 'media_zone', label: 'Media Zone', defaultColor: '#DD6B20' },
  { value: 'other', label: 'Other', defaultColor: '#A0AEC0' },
];

export interface FlowTypeOption {
  value: string;
  label: string;
  color: string;
}

export const FLOW_TYPE_OPTIONS: FlowTypeOption[] = [
  { value: 'student_movement', label: 'Student Movement', color: '#3182CE' },
  { value: 'parent_movement', label: 'Parent Movement', color: '#38A169' },
  { value: 'staff_movement', label: 'Staff Movement', color: '#805AD5' },
  { value: 'escort_required', label: 'Escort Required', color: '#DD6B20' },
];

export const stationTypeOption = (value: string): StationTypeOption =>
  STATION_TYPE_OPTIONS.find((o) => o.value === value) ??
  STATION_TYPE_OPTIONS[STATION_TYPE_OPTIONS.length - 1];

export const zoneTypeOption = (value: string): ZoneTypeOption =>
  ZONE_TYPE_OPTIONS.find((o) => o.value === value) ??
  ZONE_TYPE_OPTIONS[ZONE_TYPE_OPTIONS.length - 1];

export const flowTypeOption = (value: string): FlowTypeOption =>
  FLOW_TYPE_OPTIONS.find((o) => o.value === value) ?? FLOW_TYPE_OPTIONS[0];

/** Logical grid pitch on the abstract canvas (canvas units, not px). */
export const GRID_SIZE = 20;

/** Station glyph radius on the canvas (canvas units). */
export const STATION_RADIUS = 22;
